import React from 'react';
import { Box, Button, CircularProgress, IconButton, LinearProgress, Tooltip, Typography } from '@mui/material';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import { ClientDraftListItem } from '@/types/client';

const miniInfoCardSx = {
  borderRadius: '10px',
  bgcolor: '#fff',
  border: '1px solid #dde2e7',
  px: 1,
  py: 0.7,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
} as const;

const miniInfoValueSx = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '0.84rem',
  fontWeight: 700,
  lineHeight: '20px',
  minHeight: 20,
  color: '#94a3b8',
} as const;

const miniInfoLabelSx = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '0.75rem',
  lineHeight: '16px',
  color: '#9aa5b1',
} as const;

interface DraftClientCardProps {
  draft: ClientDraftListItem;
  onContinue: (draftId: string) => void;
  onDelete?: (draftId: string) => void;
  isDeleting?: boolean;
}

export const DraftClientCard = ({ draft, onContinue, onDelete, isDeleting = false }: DraftClientCardProps) => {
  const displayName = draft.fullName?.trim() || 'Untitled draft';
  const email = draft.email?.trim() || '—';
  const locationDisplay = draft.location?.trim() || '—';

  return (
    <Box
      onClick={() => onContinue(draft.id)}
      sx={{
        borderRadius: '16px',
        border: '2px solid #aab3bd',
        bgcolor: '#aab3bd',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
        cursor: 'pointer',
        transition: 'box-shadow 0.18s ease, transform 0.18s ease',
        '&:hover': {
          boxShadow: '0 6px 18px rgba(16, 24, 40, 0.09)',
          transform: 'translateY(-1px)',
        },
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1,
          bgcolor: '#aab3bd',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box
          sx={{
            px: 1.1,
            py: 0.2,
            borderRadius: '999px',
            bgcolor: '#fff',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            minWidth: 'fit-content',
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: '#94a3b8',
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.78rem',
              fontWeight: 600,
              color: '#64748b',
              lineHeight: 1,
            }}
          >
            Draft
          </Typography>
        </Box>

        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          {onDelete && (
            <Tooltip title="Delete draft">
              <span>
                <IconButton
                  aria-label={`Delete draft ${displayName}`}
                  disabled={isDeleting}
                  onClick={(event) => {
                    event.stopPropagation();
                    onDelete(draft.id);
                  }}
                  sx={{
                    width: 34,
                    height: 34,
                    bgcolor: 'rgba(254, 226, 226, 0.95)',
                    color: '#dc2626',
                    '&:hover': {
                      bgcolor: '#fee2e2',
                      color: '#b91c1c',
                    },
                    '&.Mui-disabled': {
                      bgcolor: 'rgba(254, 226, 226, 0.7)',
                      color: 'rgba(220, 38, 38, 0.65)',
                    },
                  }}
                >
                  {isDeleting ? (
                    <CircularProgress size={17} thickness={5} sx={{ color: 'inherit' }} />
                  ) : (
                    <DeleteOutlineRoundedIcon sx={{ fontSize: 19 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          mt: 0,
          px: 1.75,
          pt: 1.75,
          pb: 2.35,
          bgcolor: '#fff',
          borderTopLeftRadius: '14px',
          borderTopRightRadius: '14px',
          borderBottomLeftRadius: '14px',
          borderBottomRightRadius: '14px',
        }}
      >
        <Typography
          component="div"
          sx={{
            width: '100%',
            textAlign: 'left',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 500,
            fontSize: { xs: '1.15rem', sm: '1.25rem' },
            lineHeight: 1.08,
            color: '#111827',
            letterSpacing: '-0.02em',
          }}
        >
          {displayName}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mt: 1.1,
            flexWrap: 'nowrap',
            minWidth: 0,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          {email !== '—' && (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, minWidth: 0, flex: '1 1 auto' }}>
              <EmailOutlinedIcon sx={{ fontSize: 14, color: '#9aa5b1' }} />
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.8rem',
                  color: '#9aa5b1',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {email}
              </Typography>
            </Box>
          )}
          {locationDisplay !== '—' && (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, flexShrink: 0 }}>
              <LocationOnOutlinedIcon sx={{ fontSize: 14, color: '#9aa5b1' }} />
              <Typography
                sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.8rem', color: '#9aa5b1', whiteSpace: 'nowrap' }}
              >
                {locationDisplay}
              </Typography>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            mt: 1.5,
            borderRadius: '12px',
            bgcolor: '#f4f6f8',
            px: 1.2,
            py: 1.15,
          }}
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 0.75 }}>
            <Box sx={{ ...miniInfoCardSx, gridColumn: { xs: '1 / -1', sm: 'auto' } }}>
              <Typography sx={miniInfoValueSx}>—</Typography>
              <Typography sx={miniInfoLabelSx}>Last Session</Typography>
            </Box>
            <Box sx={miniInfoCardSx}>
              <Typography sx={miniInfoValueSx}>—</Typography>
              <Typography sx={miniInfoLabelSx}>Upcoming Session</Typography>
            </Box>
            <Box sx={miniInfoCardSx}>
              <Typography sx={miniInfoValueSx}>—</Typography>
              <Typography sx={miniInfoLabelSx}>Invoice Status</Typography>
            </Box>
          </Box>

          <Box
            sx={{
              mt: 1.15,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.65 }}>
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.85rem', color: '#9aa5b1' }}>
                  Questionnaire Progress
                </Typography>
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.95rem', fontWeight: 700, color: '#94a3b8' }}>
                  0%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={0}
                sx={{
                  height: 7,
                  borderRadius: '999px',
                  bgcolor: '#e3e8ee',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: '999px',
                    bgcolor: '#c3ccd4',
                  },
                }}
              />
            </Box>
          </Box>
        </Box>

        <Button
          onClick={(event) => {
            event.stopPropagation();
            onContinue(draft.id);
          }}
          variant="contained"
          fullWidth
          sx={{
            mt: 1.15,
            mb: 0.35,
            minHeight: 38,
            borderRadius: '10px',
            bgcolor: '#25a8da',
            color: '#fff',
            border: 'none',
            fontFamily: 'Inter, sans-serif',
            fontSize: '0.82rem',
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: '#1498ca',
              boxShadow: 'none',
            },
          }}
        >
          Complete Profile
        </Button>
      </Box>
    </Box>
  );
};
